import React from 'react';
import { ArrowRight, TrendingUp, Smartphone, Star } from 'lucide-react';
import { Button } from '../ui/button';

const Hero = () => {
  const scrollToForm = () => {
    document.getElementById('app-growth-lead-form')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToProof = () => {
    document.getElementById('app-growth-proof')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="app-growth-hero"
      className="relative pt-28 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50"
    >
      {/* Soft decorative blobs */}
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100/60 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          {/* Left: Copy */}
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
              Performance marketing for mobile apps
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Performance marketing for apps that need{' '}
              <span className="text-blue-600">more users, revenue, and repeat growth.</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-xl">
              Upscale helps B2C app companies improve paid acquisition, app-store conversion,
              subscription revenue, and retention — with a practical growth system built around
              data, testing, and commercial outcomes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-primary-cta"
                onClick={scrollToForm}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 group shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Book an App Growth Review
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                onClick={scrollToProof}
                variant="outline"
                size="lg"
                className="border-blue-200 text-blue-700 hover:bg-blue-50 text-lg px-8 py-6"
              >
                See how we improve app growth
              </Button>
            </div>

            <p className="text-sm text-gray-500 max-w-md">
              A focused 30-minute review of your app funnel, acquisition channels, tracking setup,
              and growth opportunities.
            </p>
          </div>

          {/* Right: Dashboard mockup composite */}
          <div className="relative">
            {/* Phone frame */}
            <div className="relative z-20 mx-auto w-56 h-[440px] bg-blue-600 rounded-[2.5rem] border-[6px] border-blue-700 shadow-2xl p-3">
              <div className="w-full h-full bg-gradient-to-br from-white to-blue-50 rounded-[1.8rem] p-4 flex flex-col justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold">
                      A
                    </div>
                    <div>
                      <div className="h-2 w-16 bg-gray-800 rounded" />
                      <div className="h-1.5 w-10 bg-gray-300 rounded mt-1" />
                    </div>
                  </div>
                  <div className="h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-end p-3">
                    <TrendingUp className="text-white/90" size={22} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white rounded-lg p-2 border border-gray-100">
                      <div className="text-[10px] text-gray-500">MAU</div>
                      <div className="text-sm font-bold text-gray-900">18.4K</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 border border-gray-100">
                      <div className="text-[10px] text-gray-500">Retention</div>
                      <div className="text-sm font-bold text-blue-600">+22%</div>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-600 rounded-lg py-2 text-center text-white text-xs font-semibold">
                  Continue
                </div>
              </div>
            </div>

            {/* Floating dashboard card 1 — Ranking */}
            <div className="absolute -top-4 -left-4 lg:-left-8 z-30 bg-white text-gray-900 rounded-xl shadow-2xl p-4 w-52 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <Star className="text-blue-600 fill-blue-600" size={14} />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  App Store Rank
                </span>
              </div>
              <div className="flex items-end justify-between">
                <div className="text-2xl font-bold">#4</div>
                <div className="flex items-center gap-1 text-blue-600 text-xs font-semibold">
                  <TrendingUp size={12} /> +18
                </div>
              </div>
              <div className="mt-3 flex items-end gap-1 h-10">
                {[35, 45, 32, 55, 48, 68, 78].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 bg-gradient-to-t from-blue-600 to-blue-400 rounded-sm"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>

            {/* Floating dashboard card 2 — CAC vs LTV */}
            <div className="absolute -bottom-6 -right-2 lg:-right-8 z-30 bg-white text-gray-900 rounded-xl shadow-2xl p-4 w-56 border border-blue-100">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                CAC vs LTV
              </div>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600">CAC</span>
                    <span className="font-semibold">$0.94</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full">
                    <div className="h-1.5 bg-blue-400 rounded-full" style={{ width: '28%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600">LTV</span>
                    <span className="font-semibold text-blue-600">$14.20</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full">
                    <div className="h-1.5 bg-blue-600 rounded-full" style={{ width: '92%' }} />
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">Ratio</span>
                <span className="text-sm font-bold text-blue-600">15.1×</span>
              </div>
            </div>

            {/* Floating: revenue card */}
            <div className="hidden md:block absolute top-1/2 -right-2 lg:right-0 -translate-y-8 z-30 bg-white border border-blue-100 rounded-xl shadow-xl p-3 w-40">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">
                Subscription MRR
              </div>
              <div className="text-lg font-bold text-gray-900 mt-1">€48.2K</div>
              <div className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                <TrendingUp size={10} /> +60% YoY
              </div>
            </div>

            {/* App icon float */}
            <div className="hidden lg:flex absolute -left-6 bottom-24 z-30 w-14 h-14 bg-white rounded-2xl shadow-xl items-center justify-center border border-blue-100">
              <Smartphone className="text-blue-700" size={22} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
